import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import logger from '../../../utils/logger';
import {
  resetSessionBoundaryData,
  setSessionStart,
} from '../sessionBoundary/sessionBoundary.slice';
import {
  AgentMode,
  AgentModeEnteredBy,
  DEFAULT_AGENT_MODE,
  DEFAULT_ENTERED_BY,
  SLICE_NAME,
} from './agentMode.constants';

export interface AgentModeState {
  mode: AgentMode;
  /**
   * HITL-1650 — who put us in `mode`. Only an agent's own `Intercept` latches;
   * see the constant's own reasoning and the guard in `setAgentMode`.
   */
  enteredBy: AgentModeEnteredBy;
}

export const initialState: AgentModeState = {
  mode: DEFAULT_AGENT_MODE,
  enteredBy: DEFAULT_ENTERED_BY,
};

/**
 * Back to the silent default. Called at every point a *new* HITL session begins
 * — never at session end and never on `car_exit`: a takeover lets the car leave
 * while the same session's order is still being finished, and resetting the
 * control state there is exactly what caused the dropped-order + cart-revert
 * incident (see the reasoning block in CustomerPulse).
 *
 * "A new session begins" is deliberately taken from the same three signals the
 * rest of the app already uses, rather than re-derived from the message stream:
 *
 *   - `setSessionStart` — the task-router flow.
 *   - `resetSessionBoundaryData` — restaurant change and the task-router WS
 *     teardown, both between orders.
 *   - `resetAgentMode` — dispatched from useSessionStatus beside `resetAI()`
 *     and `resetHypothesisFrame()`, which is the single-restaurant flow's
 *     session start.
 *
 * An earlier draft watched `messageReceived` for `car_enter` itself and needed
 * an `isCartEmpty` flag plumbed through the message payload to avoid resetting
 * while the previous order was still being finished. That work is already done
 * upstream: sessionBoundary only starts a session when `!state.sessionId`, so an
 * order still in flight keeps the old session and none of these three fire. The
 * flag, its two middleware call sites and a fourth definition of "the cart is
 * empty" all went away with it.
 */
const resetToDefault = (state: AgentModeState) => {
  if (state.mode !== DEFAULT_AGENT_MODE) {
    logger.info(`Agent mode reset to ${DEFAULT_AGENT_MODE} on session start`);
  }
  state.mode = DEFAULT_AGENT_MODE;
  // Reset with the mode, not separately. A stale `system` left on a fresh
  // Shadow would make the NEXT session's first agent press unlatchable, and the
  // log line above deliberately stays keyed on the mode alone — `enteredBy`
  // moving without the mode is not a state change worth a line.
  state.enteredBy = DEFAULT_ENTERED_BY;
};

export const agentModeSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    /**
     * The whole of what pressing the switch does.
     *
     * It emits **nothing**. The mode is permission — it unlocks the agent's
     * surfaces — while the wire events stay where they already are, on the
     * agent's real actions (`agent_interception`, typed by what was done, plus
     * the POS's one-shot `agent_first_interception`).
     *
     * That asymmetry is the point: emitting here would fail *dangerous*, since
     * an accidental press would silence the AI and leave nobody serving the
     * customer. Emitting on the first real action fails *safe* — the AI keeps
     * working until a human actually does something.
     */
    setAgentMode: (state, action: PayloadAction<AgentMode>) => {
      // ONE-WAY. Choosing Intercept is the decision, and it stands for the rest
      // of the session.
      //
      // An earlier draft let the agent walk back out of Intercept until they had
      // actually acted, tracked by an `engaged` latch set on the first
      // `agent_interception`. It bought a narrow undo — a mis-press, corrected
      // before doing anything — and charged for it everywhere else: two
      // indistinguishable Intercept states (armed and engaged) that every later
      // consumer had to know the difference between, a second piece of state to
      // reset, and a switch that could disable its own segments mid-order.
      //
      // The undo was worth less than it looked. Selecting a mode emits nothing,
      // so a mis-press reaches neither the customer nor the POS; all it costs is
      // the Shadow safety net for the current order, and the agent can still
      // simply not act. One state, one rule, and no consumer has to ask how far
      // into Intercept the agent is.
      //
      // HITL-1650 narrowed this by one term, and only one: the lock now asks
      // WHOSE Intercept it is. A console-armed Intercept (the AI asked for a
      // human) is not a decision the agent made, so refusing to let them leave
      // it would be the console overruling them on their own console. Their own
      // press still stands for the session, exactly as before.
      //
      // Adopting a console-armed Intercept — `system` becoming `agent` — is
      // reachable here, but NOT by pressing the highlighted Intercept segment:
      // `AgentModeSwitch.select()` returns early on `next === mode`, so that
      // press does nothing at all. The routes that reach it are a press on
      // Shadow and back, or `usePromoteDraftCart`'s take-over from Shadow.
      //
      // Which matters less than it sounds, because a press is not what holds
      // the agent to an Intercept they did not choose: `selectIsAgentModeLocked`
      // locks the switch as soon as anything has actually gone out on the order,
      // whether or not `enteredBy` ever changed.
      if (state.mode === AgentMode.intercept && state.enteredBy === 'agent') {
        return;
      }
      state.mode = action.payload;
      // This action is the AGENT acting, at every call site it has: the switch
      // and "Take over with My Cart". The console's own route is `armIntercept`
      // below, deliberately a separate action rather than an argument here —
      // an argument would make every existing caller restate what they are, and
      // the one that forgot would silently hand the agent an unlatchable mode.
      state.enteredBy = 'agent';
    },

    /**
     * HITL-1650 — the console arms `Intercept` because the AI asked for a human.
     *
     * Everything `setAgentMode` does NOT do applies here twice over: this puts
     * no `agent_interception` on the wire, sends no TTS, submits no order and
     * never touches `isAIAutoMode` — that flag is the AI's own control state,
     * and writing it from the console to chase a display condition is the
     * dropped-order incident behind MR !1534. Arming is permission, not action:
     * the agent arrives with the gates open and still chooses whether to walk
     * through them.
     *
     * Refuses to overwrite an `Intercept` that is already in place, whoever set
     * it. If the agent is already there, this has nothing to add and would only
     * downgrade their own decision to the console's; if the console is already
     * there, it is a repeat.
     */
    armIntercept: (state) => {
      if (state.mode === AgentMode.intercept) return;
      state.mode = AgentMode.intercept;
      state.enteredBy = 'system';
    },

    /**
     * Back to Shadow. Dispatched from TWO places, both meaning "the thing this
     * mode was turned up for is over":
     *
     *   - `useSessionStatus`, beside `resetAI()` and `resetHypothesisFrame()` —
     *     the single-restaurant flow's session start. sessionBoundary sets
     *     `sessionStatus` to `start` inline on `car_enter` without dispatching
     *     `setSessionStart`, so that path needs its own signal, and dispatching
     *     it next to the AI slice's own reset keeps the mode and the AI control
     *     state it sits on top of from disagreeing about which session they are
     *     in.
     *   - `OrderActions.resetStatesAfterFinalOrderSubmit` (HITL-1619) — the
     *     order the agent took over is finished or cancelled. The mode was the
     *     one piece of control state that chain was leaving behind: the cart,
     *     the dialog, the hypothesis and the AI status band all clear there, so
     *     an agent who finished an order carried `Intercept` into whatever came
     *     next. A staging session showed the cost — a new customer started
     *     ordering with no car cycle in between and the console sat in
     *     `Intercept` with the previous order's state for nine minutes.
     *
     * An action rather than `setAgentMode(shadow)`, which the one-way guard
     * above would refuse — and refuse in exactly the case that needs it.
     */
    resetAgentMode: resetToDefault,
  },
  extraReducers: (builder) => {
    // The task-router flow.
    builder.addCase(setSessionStart, resetToDefault);

    // Restaurant change and the task-router WS teardown; both between orders.
    builder.addCase(resetSessionBoundaryData, resetToDefault);
  },
});

export const { setAgentMode, armIntercept, resetAgentMode } =
  agentModeSlice.actions;

export default agentModeSlice.reducer;
